import React, { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { fetcher } from "../../components/Overview/Overview";
import Dialog from "rc-dialog";
import DatePicker from "react-datepicker-new";
import moment from "moment";
import Select, { createFilter } from "react-select-new";
import cx from "classnames";
import { useDebounce } from "../../utils/debounce";
import { eventTypeToNL, GENDER_ABBREVIATIONS } from "../../constants";

// remove things like ": e-learning" from strings as they are superfluous
export const filterEventTypeName = (name, type) => {
    const nlType = eventTypeToNL(type);

    const ret = nlType !== type ? filterEventTypeName(name, nlType) : name;

    return ret.replace(`: ${type}`, "").replace(`:${type}`, "").replace(`${type}`, "");
};

// convert a start_datetime based on if it can be in the past (or should be now)
export const dateOrNow = (start_datetime) => {
    let startDate = new Date(start_datetime); // implicit cast, even when it is a moment() object (it sometimes is)

    // separate if statement to override for elearning (might need to revert this in the future when is realized that it is kind of strange)
    if (start_datetime && startDate < new Date()) {
        startDate = new Date();
    }

    return startDate;
};
// We need a way to get the ISO formatted Date
const createIsoFormat = (datetime) => {
    let offset = datetime.getTimezoneOffset() * 60 * 1000;
    let localDateTime = new Date(datetime - offset);
    let fullIsoDate = localDateTime.toISOString();

    return fullIsoDate.slice(0, 19);
};

// Deleting a specific instructor from the list, if this happens to be the primary instructor clear it
const deleteInstructor = (instructorUUID, primaryInstructorUUID, instructors) => {
    let newSelectedInstructors = [...instructors.filter((instr) => instr.uuid !== instructorUUID)];
    let newPrimaryInstructor = null;

    if (instructorUUID === primaryInstructorUUID) {
        // This means we need to change or delete the primary instructor
        if (newSelectedInstructors.length > 0) {
            newPrimaryInstructor = newSelectedInstructors[0];
        }
    }

    return {
        newPrimaryInstructor,
        newSelectedInstructors
    };
};

// Ensures the value of the supplied setter is returned to false
// Currently used to reset detected changes for date and time inputs
const toggleChangeAnimation = (setter, currentValue) => {
    if (typeof setter === "function" && currentValue) {
        setTimeout(() => setter(false), 1000);
        // If the current value is true we need to add a class which triggers an animation
        return " input-changed";
    }
    return "";
};

export const Event = React.memo((props) => {
    const event = props.event;

    const [dateChanged, setDateChanged] = useState(false);
    const [timeFromChanged, setTimeFromChanged] = useState(false);
    const [timeToChanged, setTimeToChanged] = useState(false);
    const [locationChanged, setLocationChanged] = useState(false);
    const [instructorsChanged, setInstructorsChanged] = useState(false);

    const [confirmSplit, setConfirmSplit] = useState(false);
    const [splitEvent, setSplitEvent] = useState(false);
    const [markDelete, setMarkDelete] = useState(false);

    const [startDateOffset, setStartDateOffset] = useState(false);

    const [dateStart, setDateStart] = useState(props.dateStart);
    const [timeFrom, setTimeFrom] = useState(
        event.start_datetime ? Intl.DateTimeFormat("nl-NL", { hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(event.start_datetime)) : null
    );
    const [timeTo, setTimeTo] = useState(
        event.end_datetime ? Intl.DateTimeFormat("nl-NL", { hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(event.end_datetime)) : null
    );

    const [selectedInstructors, setSelectedInstructors] = useState(event.event_roles[0].instructors || []);
    const [primaryInstructor, setPrimaryInstructor] = useState(event.primary_instructor);
    const [location, setLocation] = useState(event.location);

    const [eventUUID, setEventUUID] = useState(event.uuid);
    const [competenceProfileUUID, setCompetenceProfileUUID] = useState(props.competenceProfileUUID);
    const [eventErrors, setEventErrors] = useState([]);

    const [updatedEvent, setUpdatedEvent] = useState({
        ...props.event,
        current_index: props?.currentIndex
    });

    const { data: selectableInstructors } = useSWR(
        () =>
            competenceProfileUUID && event.template && props.trainer?.uuid
                ? `/v1/competenceprofiles/${competenceProfileUUID}/eventtemplates/${event.template}/instructors/?fields=uuid,gender,full_name,nibhv_number&pagination=false&trainer=${props.trainer?.uuid}&is_active=true`
                : null,
        fetcher
    );

    const update = useDebounce(updatedEvent, 500);

    const shouldUpdate = useRef(true);

    const datepickerRef = useRef(null);

    // when props change, we need to update vars
    useEffect(() => {
        shouldUpdate.current = false;

        setDateChanged(props.event.date_changed);
        setTimeFromChanged(props.event.time_from_changed);
        setTimeToChanged(props.event.time_to_changed);
        setLocationChanged(props.event.location_changed);
        setInstructorsChanged(props.event.instructors_changed);

        const event = props.event;

        setDateStart(event.dateStart);
        setTimeFrom(
            event.start_datetime ? Intl.DateTimeFormat("nl-NL", { hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(event.start_datetime)) : null
        );
        setTimeTo(event.end_datetime ? Intl.DateTimeFormat("nl-NL", { hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(event.end_datetime)) : null);
        setLocation(event.location);

        const instructors = event?.event_roles?.[0]?.instructors;
        if (instructors && instructors.length !== selectedInstructors.length) {
            setSelectedInstructors(instructors);
            setPrimaryInstructor(instructors.find((i) => i.primary));
        }
        setCompetenceProfileUUID(props.competenceProfileUUID);
        setEventUUID(event.uuid);
    }, [
        props.event,
        props.event.dateStart,
        props.event.start_datetime,
        props.event.end_datetime,
        props.event.location,
        props.event.event_roles[0].instructors,
        props.competenceProfileUUID,
        shouldUpdate,
        selectableInstructors
    ]);

    // handle currentIndex prop differently
    useEffect(() => {
        shouldUpdate.current = false;
        setUpdatedEvent((curState) =>
            Object.assign({}, curState, {
                current_index: props?.currentIndex
            })
        );
    }, [props?.currentIndex]);

    // when the instructors change, update appropriately
    useEffect(() => {
        const primaryInstructor = selectedInstructors?.length > 0 ? selectedInstructors[0] : false;
        setPrimaryInstructor(primaryInstructor);

        setUpdatedEvent((curState) =>
            Object.assign({}, curState, {
                event_roles: [
                    {
                        ...curState.event_roles[0],
                        instructors: selectedInstructors
                    }
                ],
                primary_instructor: primaryInstructor
            })
        );
    }, [selectedInstructors]);

    // When an event updates it selectable instructors, pass it to our parent so it is usable there
    useEffect(() => {
        if (selectableInstructors) {
            props?.setEventAllowedTrainers((curState) => {
                return {
                    ...curState,
                    [props.currentIndex]: selectableInstructors
                };
            });
        }
    }, [selectableInstructors, props?.setEventAllowedTrainers]);

    useEffect(() => {
        setUpdatedEvent((curState) =>
            Object.assign({}, curState, {
                location: location
            })
        );
    }, [location]);

    useEffect(() => {
        setUpdatedEvent((curState) =>
            Object.assign({}, curState, {
                mark_delete: markDelete
            })
        );
    }, [markDelete]);

    useEffect(() => {
        shouldUpdate.current = true;
        setUpdatedEvent((curState) =>
            Object.assign({}, curState, {
                split_event: splitEvent
            })
        );
    }, [splitEvent]);

    useEffect(() => {
        const mergeEvent = {
            dateStart,
            timeFrom,
            timeTo,
            start_datetime: false,
            end_datetime: false
        };

        let startTime = false;
        let endTime = false;

        if (dateStart && timeFrom) {
            startTime = new Date(dateStart.setHours(...timeFrom.split(":")));
            mergeEvent.start_datetime = createIsoFormat(startTime);
        }

        if (dateStart && timeTo) {
            endTime = new Date(dateStart.setHours(...timeTo.split(":")));
            mergeEvent.end_datetime = createIsoFormat(endTime);
        }

        if (dateStart && startTime) {
            if (!endTime || endTime < startTime) {
                // # 2001 - Temporarily not convert the end date based on the start date as the logic for this (from business perspective) is flawed
                // we might re-introduce it in the future
                // mergeEvent.end_datetime = createIsoFormat(startTime);
            }
        }
        setUpdatedEvent((curState) => Object.assign({}, curState, mergeEvent));

        if (dateStart) {
            // Set the difference in hours of the start of the event compared to now
            setStartDateOffset(moment(dateStart).diff(moment(), "hours"));
        }
    }, [dateStart, timeFrom, timeTo]);

    useEffect(() => {
        // debounced update call
        if (shouldUpdate.current) {
            props.updateScheduledEvent(update);
        }
    }, [update, shouldUpdate]);

    useEffect(() => {
        setEventErrors(props.eventErrors);
    }, [props.eventErrors]);

    return (
        <>
            {confirmSplit && (
                <Dialog
                    title="Toetsmoment splitsen"
                    onClose={() => {
                        setConfirmSplit(false);
                    }}
                    visible={true}
                    animation="fade"
                    footer={[
                        <button
                            type="button"
                            className="btn btn-light"
                            key="no"
                            onClick={() => {
                                setConfirmSplit(false);
                            }}
                        >
                            Nee, toetsmoment niet splitsen
                        </button>,
                        <button
                            type="button"
                            className="btn btn-primary"
                            key="yes"
                            onClick={() => {
                                setConfirmSplit(false);
                                setSplitEvent(true);
                            }}
                        >
                            Ja, toetsmoment splitsen
                        </button>
                    ]}
                >
                    <p>
                        Weet je zeker dat je <strong>{event.name}</strong> wilt splitsen in de volgende losse toetsmomenten: <br />
                    </p>
                    <ul className={"default"}>
                        {props.splittableEvents
                            .find((e) => e.uuid === event.template)
                            ?.children?.map((e) => {
                                return <li key={e.template}>{e.name}</li>;
                            })}
                    </ul>
                    <p>
                        <strong>Let op!</strong> Je kunt deze actie niet ongedaan maken.
                    </p>
                </Dialog>
            )}
            <div className="form-section__row row">
                <div className="col-12">
                    <div className="event">
                        <div className="event__header flex flex-direction-column flex-gap-xsmall">
                            <div className="flex flex-justify-space-between">
                                <h2 className="event__title">
                                    {event?.type === "knowledge-exam" && <i className="fa fa-graduation-cap event__title-icon" />}
                                    {event?.type === "practice" && <i className="fa fa-group event__title-icon" />}
                                    {event.name} &nbsp;
                                    {event.uuid && <span className="tag">Eerder opgeslagen</span>}
                                </h2>
                                <div className="flex flex-gap-small flex-shrink-0">
                                    {/*<a href={"#"} onClick={e => {e.preventDefault(); setMarkDelete(true);}} >[delete]</a>*/}
                                    {props.splittableEvents.find((e) => e.uuid === event.template) && (
                                        <a
                                            className="event__split"
                                            href={"#"}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setConfirmSplit(true);
                                            }}
                                        >
                                            Splitsen <i className="fa fa-expand" />
                                        </a>
                                    )}
                                    {typeof props?.deleteEvent === "function" && (
                                        <a
                                            href={"#"}
                                            className="link link--tertiary"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                props?.deleteEvent();
                                            }}
                                        >
                                            Verwijderen <i className="fa fa-times-circle" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form-section__row row">
                            <div className="col-2">
                                <label className="form-input__label">Datum</label>
                                <DatePicker
                                    name="dateStart"
                                    onChange={(change) => {
                                        shouldUpdate.current = true;
                                        setDateStart(change);
                                    }}
                                    ref={(ref) => (datepickerRef.current = ref)}
                                    onKeyDown={(event) => {
                                        if (event.target.value) {
                                            let value = event.target.value;
                                            if (event.keyCode === 9 && value.length > 2 && value.length < 6) {
                                                let splittedValue = value.split("-");
                                                let day = splittedValue[0];
                                                let month = splittedValue[1];
                                                let year = moment().format("YYYY");
                                                let newDate = year + "-" + month + "-" + day;

                                                shouldUpdate.current = true;
                                                event.target.value = newDate;
                                                setDateStart(new Date(newDate));
                                            }
                                        }

                                        // workaround for closing on tab (undocumented logic)
                                        if (event.key === "Tab") {
                                            datepickerRef.current.setOpen(false, true);
                                        }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    onChangeRaw={(event) => {
                                        if (event.target.value) {
                                            let value = event.target.value.split("-").join("");
                                            let newDate = value.substring(0, 2);

                                            if (value.length >= 3) {
                                                newDate += "-" + value.substring(2, 4);
                                            }

                                            if (value.length >= 5) {
                                                let checkYear = value.substring(4, 8);
                                                if (checkYear.length === 2 && checkYear !== "19" && checkYear !== "20") {
                                                    newDate += "-20" + value.substring(4, 8);
                                                } else {
                                                    newDate += "-" + value.substring(4, 8);
                                                }

                                                // returns a number if it is valid, so > 0 is valid (is a quickfix)
                                                if (Date.parse(moment(newDate, "DD-MM-YYYY").format()) > 0) {
                                                    setDateStart(moment(newDate, "DD-MM-YYYY").toDate());
                                                }
                                            }
                                            event.target.value = newDate;
                                        }
                                    }}
                                    className={cx("form-control" + (dateChanged ? toggleChangeAnimation(setDateChanged, dateChanged) : ""), {
                                        "form-control--error": eventErrors?.findIndex((o) => o?.field === "start_datetime") >= 0
                                    })}
                                    dateFormat="dd-MM-yyyy"
                                    required={true}
                                    placeholderText="-- -- ----"
                                    selected={dateStart}
                                    autoComplete="off"
                                    minDate={props.minDate}
                                    maxDate={props.maxDate}
                                    preventOpenOnFocus={true}
                                    disabled={props.isEditable === false}
                                />
                            </div>
                            <div className="form-input col-2">
                                <label className="form-input__label">Van</label>
                                <input
                                    type="time"
                                    value={timeFrom ? timeFrom : ""}
                                    className={cx("form-control" + (timeFromChanged ? toggleChangeAnimation(setTimeFromChanged, timeFromChanged) : ""), {
                                        "form-control--error": eventErrors?.findIndex((o) => o?.field === "start_datetime") >= 0
                                    })}
                                    onFocus={(e) => {
                                        if (e.target.value === "") {
                                            setTimeFrom("00:00");
                                        }
                                    }}
                                    onChange={(e) => {
                                        shouldUpdate.current = true;
                                        setTimeFrom(e.target.value);
                                    }}
                                    disabled={!dateStart || props.isEditable === false}
                                />
                            </div>
                            <div className="form-input col-2">
                                <label className="form-input__label">Tot</label>
                                <input
                                    type="time"
                                    value={timeTo ? timeTo : ""}
                                    min={timeFrom ? timeFrom : ""}
                                    className={cx("form-control" + (timeToChanged ? toggleChangeAnimation(setTimeToChanged, timeToChanged) : ""), {
                                        "form-control--error": eventErrors?.findIndex((o) => o?.field === "end_datetime") >= 0
                                    })}
                                    onFocus={(e) => {
                                        if (e.target.value === "") {
                                            setTimeTo("00:00");
                                        }
                                    }}
                                    onChange={(e) => {
                                        shouldUpdate.current = true;
                                        setTimeTo(e.target.value);
                                    }}
                                    disabled={!dateStart || props.isEditable === false}
                                />
                            </div>

                            <div className="form-input col-3">
                                <label className="form-input__label">Locatie</label>
                                {!props.trainer ? (
                                    <input className={"form-control form-control-md form-control--disabled"} disabled type={"text"} value={"Kies eerst een opleider"} />
                                ) : (
                                    <Select
                                        name="location"
                                        value={location}
                                        className={"react-select-new" + (locationChanged ? toggleChangeAnimation(setLocationChanged, locationChanged) : "")}
                                        classNamePrefix="react-select"
                                        onChange={(location) => {
                                            shouldUpdate.current = true;
                                            setLocation(location);
                                        }}
                                        controlShouldRenderValue={true}
                                        options={props.locations}
                                        getOptionLabel={(location) =>
                                            `${location.name}, ${location.street_name} ${location.number}, ${location.postal_code}, ${location.city}`
                                        }
                                        getOptionValue={(location) => location}
                                        isClearable={true}
                                        backspaceRemovesValue={true}
                                        placeholder={""}
                                        noOptionsMessage={() => "Geen resultaten"}
                                        isDisabled={props.isEditable === false}
                                    />
                                )}
                            </div>

                            <div className="form-input col-3">
                                <label className="form-input__label">Instructeur(s)</label>
                                {!props.trainer || !selectableInstructors ? (
                                    <input className={"form-control form-control-md form-control--disabled"} disabled type={"text"} value={"Kies eerst een opleider"} />
                                ) : (
                                    <Select
                                        name="instructors"
                                        value={selectedInstructors}
                                        className={cx(
                                            "react-select-new" +
                                                (instructorsChanged && selectedInstructors.length > 0
                                                    ? toggleChangeAnimation(setInstructorsChanged, instructorsChanged)
                                                    : ""),
                                            { "form-control--error": eventErrors?.findIndex((o) => o?.field === "event_roles") >= 0 }
                                        )}
                                        classNamePrefix="react-select"
                                        onChange={(values) => {
                                            shouldUpdate.current = true;
                                            setSelectedInstructors(values);
                                        }}
                                        isMulti={true}
                                        controlShouldRenderValue={false}
                                        options={selectableInstructors}
                                        getOptionLabel={(instr) =>
                                            `${instr.full_name}${instr.gender ? " (" + GENDER_ABBREVIATIONS[instr.gender] + ")" : ""}\n${instr.nibhv_number}`
                                        }
                                        getOptionValue={(instr) => instr.uuid}
                                        isClearable={false}
                                        backspaceRemovesValue={false}
                                        placeholder={selectedInstructors.length > 0 ? `Aantal gekozen: ${selectedInstructors.length}` : "Klik om te kiezen"}
                                        noOptionsMessage={() => "Geen resultaten"}
                                        filterOption={createFilter({
                                            ignoreCase: true,
                                            ignoreAccents: true,
                                            trim: false,
                                            matchFromStart: false,
                                            stringify: (option) => `${option.label} ${option.data.nibhv_number.replaceAll("-", "")}`
                                        })}
                                        isDisabled={props.isEditable === false}
                                        onKeyDown={(e) => {
                                            if (e.key === "Tab") {
                                                e.preventDefault();

                                                const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
                                                const focusables = Array.from(document.querySelectorAll(focusableElements));
                                                const currentIndex = focusables.indexOf(e.target);
                                                const nextIndex = (currentIndex + 1) % focusables.length;

                                                focusables[nextIndex + 1].focus();
                                            }
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                        {selectedInstructors?.length > 0 && (
                            <div className="trainer">
                                {selectedInstructors.map((instr) => (
                                    <div
                                        className={cx(
                                            "trainer__tag"
                                            // {"trainer__tag--primary": instr.uuid === primaryInstructor?.uuid}
                                        )}
                                        key={instr.uuid}
                                    >
                                        <svg
                                            className="trainer__icon"
                                            onClick={(e) => {
                                                // e.preventDefault();
                                                const { newPrimaryInstructor, newSelectedInstructors } = deleteInstructor(
                                                    instr.uuid,
                                                    primaryInstructor,
                                                    selectedInstructors
                                                );
                                                shouldUpdate.current = true;
                                                setSelectedInstructors(newSelectedInstructors);
                                                setPrimaryInstructor(newPrimaryInstructor);
                                            }}
                                            width="100%"
                                            height="100%"
                                            viewBox="0 0 9 9"
                                        >
                                            <path d="M7.2,0l0.8,0.8l-3.201,3.199l3.201,3.201l-0.8,0.8l-3.2,-3.201l-3.2,3.201l-0.8,-0.8l3.2,-3.201l-3.2,-3.199l0.8,-0.8l3.2,3.2l3.2,-3.2Z" />
                                        </svg>
                                        <span
                                            className="trainer__name"
                                            key={"selectedInstructor" + instr.uuid}
                                            // onClick={() => setPrimaryInstructor(instr)}
                                        >
                                            {instr.full_name}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {event.prerequisites?.map((prereqUUID) => {
                            const prereqEvent = props.scheduledEvents.filter((eventTemplate) => eventTemplate.template === prereqUUID)[0];
                            const formatOptions = { day: "numeric", month: "long", year: "numeric" };

                            const type = eventTypeToNL(prereqEvent.type);

                            let start_datetime = prereqEvent?.type === "elearning" ? dateOrNow(prereqEvent?.start_datetime) : prereqEvent?.start_datetime;

                            if (prereqEvent.start_datetime === null) {
                                return null;
                            }

                            return (
                                <React.Fragment key={prereqEvent.template}>
                                    <span className="event__notice--small">
                                        De {type} die hoort bij <strong>{filterEventTypeName(prereqEvent.name, prereqEvent.type).replace("Toetsmoment", "")}</strong> (
                                        {eventTypeToNL(event.type)})
                                        {/* FIXME: Display the start and end datetime for prerequisits, but need to check for elearnings only if its needed. */}
                                        {prereqEvent.start_datetime && (
                                            <>
                                                &nbsp;start op {start_datetime && new Intl.DateTimeFormat("nl-NL", formatOptions).format(new Date(start_datetime))}
                                                &nbsp;en moet afgerond zijn op{" "}
                                                {prereqEvent.end_datetime && new Intl.DateTimeFormat("nl-NL", formatOptions).format(new Date(prereqEvent.end_datetime))}.
                                            </>
                                        )}
                                    </span>
                                    <br />
                                </React.Fragment>
                            );
                        })}

                        {props.isEditable && startDateOffset !== false && (
                            <div className="event__notice--small">
                                {startDateOffset > 0 && startDateOffset < 120 && (
                                    <div className="feedback-msg--error">
                                        Let op: Omdat het toetsmoment binnen 5 dagen begint worden er extra kosten in rekening gebracht.
                                    </div>
                                )}
                                {startDateOffset < 0 && (
                                    <div className="feedback-msg--error">
                                        Let op: Omdat het toetsmoment in het verleden ligt worden er extra kosten in rekening gebracht.
                                    </div>
                                )}
                            </div>
                        )}
                        {eventErrors?.find((o) => o?.field === "event_roles") && (
                            <>
                                {eventErrors
                                    ?.find((o) => o?.field === "event_roles")
                                    ?.error?.instructors?.map((instructorRemark) => {
                                        return <div className={"feedback-msg--error"}>{instructorRemark}</div>;
                                    })}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
});
